import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, History, Building2, X } from "lucide-react";
import { streamPost, API } from "@/lib/stream";

const PRESETS = [
  "What's a fair remote-work stipend policy for 2026?",
  "How do I onboard managers across 4 timezones?",
  "What fair-housing training do new hires need first?",
];

const ZIGGY_AVATAR =
  "https://images.unsplash.com/photo-1740252117013-4fb21771e7ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxjb2xvcmZ1bCUyMGF2YXRhciUyMGNoYXJhY3RlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc5MDQ1MDE4Mnww&ixlib=rb-4.1.0&q=85&w=200";

const GREETING =
  "Hey, I'm Ziggy — your 24/7 HR concierge, powered by ChatGPT. Ask me anything about people ops, policies, onboarding or compliance.";
const GREETING_BACK = "Welcome back! Your previous conversation is right where you left it. What else can I help with?";

const TEAM_SIZES = ["1-50", "51-200", "201-1000", "1000+"];
const PAIN_POINTS = ["Onboarding & training staff", "Compliance & fair housing", "Retention & engagement", "Documentation overload", "All of the above"];

function getSessionId() {
  let s = localStorage.getItem("op360_chat_session");
  if (!s) {
    s = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("op360_chat_session", s);
  }
  return s;
}

const miniInput =
  "w-full bg-white rounded-xl border-2 border-slate-900/10 px-3.5 py-2.5 text-xs font-medium outline-none focus:border-[#2563EB] transition-colors placeholder:text-slate-400";

export default function AiChatbotWidget() {
  const [sessionId] = useState(getSessionId);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [restored, setRestored] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [leadForm, setLeadForm] = useState({ company_name: "", contact_name: "", work_email: "", team_size: TEAM_SIZES[0], pain_point: PAIN_POINTS[0] });
  const [leadBusy, setLeadBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/chat/history?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.messages && data.messages.length > 0) {
          setMessages([{ role: "assistant", text: GREETING_BACK }, ...data.messages]);
          setRestored(true);
        }
      })
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    const handler = (e) => send(e.detail);
    window.addEventListener("opera360:quick-ask", handler);
    return () => window.removeEventListener("opera360:quick-ask", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, showLead]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      await streamPost("/chat", { session_id: sessionId, message: msg }, (delta) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + delta };
          return copy;
        });
      });
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Hmm, my connection hiccuped. Try that again?" };
        return copy;
      });
    }
    setBusy(false);
  };

  const saveLead = async (e) => {
    e.preventDefault();
    if (leadBusy) return;
    setLeadBusy(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...leadForm, notes: "Captured mid-chat by Ziggy", source: "ziggy-chat" }),
      });
      if (res.ok) {
        setShowLead(false);
        setMessages((m) => [
          ...m,
          { role: "assistant", text: `Done — ${leadForm.company_name} is now in the Opera360 pipeline and the team has been emailed. Expect a demo invite shortly. Anything else on your mind?` },
        ]);
        setLeadForm({ company_name: "", contact_name: "", work_email: "", team_size: TEAM_SIZES[0], pain_point: PAIN_POINTS[0] });
      } else {
        setMessages((m) => [...m, { role: "assistant", text: "I couldn't file that just now — please try the registration form further down the page." }]);
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "I couldn't file that just now — please try the registration form further down the page." }]);
    }
    setLeadBusy(false);
  };

  return (
    <section id="ai-tools" className="py-24 sm:py-32 bg-white border-y-2 border-slate-900/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#2563EB]">AI Tool №1 · ChatGPT</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mt-3">
            Meet Ziggy. Your HR brain that never sleeps.
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed mt-4 max-w-lg">
            Employees ask, Ziggy answers — instantly, accurately, and always on-brand with your
            policies. Your conversation is saved, and when you're ready, Ziggy can file your
            company straight into the CRM without leaving the chat.
          </p>
          <div className="flex flex-wrap gap-2 mt-6">
            {PRESETS.map((p, i) => (
              <button
                key={p}
                data-testid={`chatgpt-preset-chip-${i}`}
                onClick={() => send(p)}
                className="px-4 py-2 rounded-full border-2 border-slate-900/10 text-xs font-bold text-slate-600 hover:border-[#2563EB] hover:text-[#2563EB] transition-colors duration-200"
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-8">
            <img src={ZIGGY_AVATAR} alt="Ziggy the HR concierge character" className="w-12 h-12 rounded-2xl object-cover border-2 border-slate-900/5" />
            <div className="text-sm">
              <div className="font-bold">Ziggy · HR Concierge</div>
              <div className="text-[#10B981] font-bold text-xs flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse-dot" /> Online now
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          data-testid="chatgpt-widget-container"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl border-2 border-slate-900/5 bg-[#FBFBF9] shadow-[0_30px_80px_-25px_rgba(15,23,42,0.25)] overflow-hidden"
        >
          <div className="flex items-center gap-2 px-5 py-3.5 bg-[#0F172A] text-white">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FF5722]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
            <span className="ml-3 text-xs font-bold tracking-widest uppercase text-slate-400">Ziggy · Live ChatGPT Session</span>
            {restored && (
              <span data-testid="chatgpt-history-restored" className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-[#F59E0B] uppercase tracking-wider">
                <History className="w-3 h-3" /> Transcript restored
              </span>
            )}
          </div>
          <div ref={scrollRef} className="chat-scroll h-80 overflow-y-auto p-5 space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={m.role === "assistant" ? "chatgpt-message-response" : undefined}
                  className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#2563EB] text-white rounded-br-md"
                      : "bg-white border-2 border-slate-900/5 text-slate-700 rounded-bl-md shadow-sm"
                  }`}
                >
                  {m.text || (busy && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : "")}
                </div>
              </div>
            ))}
          </div>

          <AnimatePresence>
            {showLead && (
              <motion.form
                data-testid="chatgpt-lead-form"
                onSubmit={saveLead}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden bg-white border-t-2 border-slate-900/5"
              >
                <div className="p-4 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-[#FF5722]" /> File your company into the CRM</span>
                    <button type="button" onClick={() => setShowLead(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
                  </div>
                  <input data-testid="chatgpt-lead-company-input" required value={leadForm.company_name} onChange={(e) => setLeadForm({ ...leadForm, company_name: e.target.value })} placeholder="Company / portfolio name" className={miniInput} />
                  <div className="grid grid-cols-2 gap-2.5">
                    <input required value={leadForm.contact_name} onChange={(e) => setLeadForm({ ...leadForm, contact_name: e.target.value })} placeholder="Your name" className={miniInput} />
                    <input data-testid="chatgpt-lead-email-input" required type="email" value={leadForm.work_email} onChange={(e) => setLeadForm({ ...leadForm, work_email: e.target.value })} placeholder="Work email" className={miniInput} />
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    <select value={leadForm.team_size} onChange={(e) => setLeadForm({ ...leadForm, team_size: e.target.value })} className={miniInput}>
                      {TEAM_SIZES.map((s) => <option key={s} value={s}>{s} people</option>)}
                    </select>
                    <select value={leadForm.pain_point} onChange={(e) => setLeadForm({ ...leadForm, pain_point: e.target.value })} className={miniInput}>
                      {PAIN_POINTS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <button data-testid="chatgpt-lead-submit" type="submit" disabled={leadBusy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#10B981] text-white text-xs font-bold hover:bg-[#0F172A] transition-colors disabled:opacity-60">
                    {leadBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Building2 className="w-3.5 h-3.5" />}
                    File it, Ziggy
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-4 border-t-2 border-slate-900/5 bg-white">
            <button
              data-testid="chatgpt-save-lead-button"
              type="button"
              onClick={() => setShowLead(!showLead)}
              title="Save my company to the CRM"
              className="p-3 rounded-full border-2 border-slate-900/10 text-slate-500 hover:border-[#10B981] hover:text-[#10B981] transition-colors duration-200 shrink-0"
            >
              <Building2 className="w-4 h-4" />
            </button>
            <input
              data-testid="chatgpt-input-field"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Ziggy an HR question…"
              className="flex-1 bg-[#F4F4F0] rounded-full px-5 py-3 text-sm font-medium outline-none focus:ring-2 ring-[#2563EB]/40 placeholder:text-slate-400"
            />
            <button
              data-testid="chatgpt-send-button"
              type="submit"
              disabled={busy}
              className="p-3 rounded-full bg-[#FF5722] text-white hover:bg-[#0F172A] transition-colors duration-300 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
